import React, { useState, useRef, useCallback } from 'react'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Document, Page } from './pdf/pdfjs'
import { colors, spacing, radius, shadow } from '../styles/theme'

interface PreviewPaneProps {
  pdfUrl: string | null
  loading: boolean
  error: string | null
}

export default function PreviewPane({ pdfUrl, loading, error }: PreviewPaneProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1.0)
  const containerRef = useRef<HTMLDivElement>(null)

  const onLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setCurrentPage(1)
  }, [])

  const zoomIn = () => setScale(s => Math.min(s + 0.25, 3.0))
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.5))
  const resetZoom = () => setScale(1.0)

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: colors.background,
      borderRadius: radius.md,
      overflow: 'hidden',
      boxShadow: shadow.sm,
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        padding: `${spacing.sm} ${spacing.md}`,
        background: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        flexWrap: 'wrap',
      }}>
        <span style={{ color: colors.textSecondary, fontSize: '12px', flex: 1, minWidth: 0 }}>
          {pdfUrl && numPages > 0 ? `Page ${currentPage} of ${numPages}` : 'Preview'}
        </span>

        {/* Page nav */}
        {numPages > 1 && (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <ToolbarButton onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage <= 1}>
              ‹
            </ToolbarButton>
            <ToolbarButton onClick={() => setCurrentPage(p => Math.min(p + 1, numPages))} disabled={currentPage >= numPages}>
              ›
            </ToolbarButton>
          </div>
        )}

        {/* Zoom controls */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <ToolbarButton onClick={zoomOut} disabled={scale <= 0.5}>−</ToolbarButton>
          <button
            onClick={resetZoom}
            style={{
              padding: '4px 8px',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: '11px',
              color: colors.textSecondary,
              cursor: 'pointer',
              minWidth: 44,
            }}
          >
            {Math.round(scale * 100)}%
          </button>
          <ToolbarButton onClick={zoomIn} disabled={scale >= 3.0}>+</ToolbarButton>
        </div>
      </div>

      {/* PDF area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: spacing.md,
          display: 'flex',
          flexDirection: 'column',
          // `safe`: a page wider than the pane starts at the left edge instead of overflowing
          // past it, where it could not be scrolled to.
          alignItems: 'safe center',
          position: 'relative',
          minHeight: 300,
        }}
      >
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 10,
          }}>
            <span style={{ color: colors.primary, fontSize: '13px' }}>Rendering…</span>
          </div>
        )}

        {error && !loading && (
          <div style={{
            padding: spacing.md,
            background: colors.errorLight,
            borderRadius: radius.md,
            color: colors.error,
            fontSize: '13px',
            textAlign: 'center',
            width: '100%',
          }}>
            {error}
          </div>
        )}

        {!pdfUrl && !loading && !error && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 200,
            color: colors.textMuted,
            fontSize: '13px',
            gap: spacing.sm,
          }}>
            <span style={{ fontSize: '32px' }}>📄</span>
            <span>Fill in a value to see the preview</span>
          </div>
        )}

        {pdfUrl && (
          <Document
            file={pdfUrl}
            onLoadSuccess={onLoadSuccess}
            loading={null}
            error={<span style={{ color: colors.error, fontSize: '13px' }}>Failed to load PDF</span>}
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={false}
            />
          </Document>
        )}
      </div>
    </div>
  )
}

function ToolbarButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: `1px solid ${colors.border}`,
        borderRadius: radius.sm,
        fontSize: '16px',
        color: disabled ? colors.textMuted : colors.text,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  )
}
